import React, { memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { crackVariants } from '../animations/variants';

interface CrackOverlayProps {
  /**
   * Visual damage stage:
   *   0 — hidden (no cracks)
   *   1 — hairline crack after first clash
   *   2 — spreading cracks after second clash
   *   3 — full fracture pattern at final impact (loser only)
   */
  stage: 0 | 1 | 2 | 3;
}

/**
 * Progressive SVG crack overlay. Stage builds up across the fight —
 * both eggs take visible damage at each clash, then the loser shatters fully.
 */
function CrackOverlayComponent({ stage }: CrackOverlayProps): React.ReactElement {
  return (
    <AnimatePresence>
      {stage > 0 && (
        <motion.div
          key={stage}
          className="absolute inset-0 pointer-events-none"
          variants={crackVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          style={{ willChange: 'transform, opacity', zIndex: 10 }}
        >
          <svg
            viewBox="0 0 200 240"
            className="w-full h-full"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ filter: 'drop-shadow(0 0 3px rgba(0,0,0,0.9))' }}
          >
            {/* ── Stage 1+: hairline crack from first clash ────────────────── */}
            <path
              d="M108,52 L100,86 L114,98 L104,128"
              stroke="white"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity="0.75"
            />
            <circle cx="107" cy="92" r="3" fill="white" opacity="0.5" />

            {/* ── Stage 2+: branching cracks from second clash ─────────────── */}
            {stage >= 2 && (
              <>
                <path
                  d="M114,98 L136,112 L126,132"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity="0.65"
                />
                <path
                  d="M100,86 L78,100 L90,114 L76,130"
                  stroke="white"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity="0.6"
                />
                <circle cx="107" cy="92" r="4.5" fill="white" opacity="0.3" />
              </>
            )}

            {/* ── Stage 3: full shatter (loser only) ───────────────────────── */}
            {stage >= 3 && (
              <>
                {/* Extend main crack further down */}
                <path
                  d="M104,128 L100,148 L86,188"
                  stroke="white"
                  strokeWidth="2.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity="0.95"
                />
                {/* Widen existing paths */}
                <path
                  d="M108,52 L100,86 L114,98 L88,142 L102,148 L84,188"
                  stroke="white"
                  strokeWidth="3.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity="0.95"
                />
                <path
                  d="M114,98 L138,110 L128,130 L145,148"
                  stroke="white"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity="0.85"
                />
                <path
                  d="M94,82 L72,98 L84,108 L70,128"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity="0.75"
                />
                <path
                  d="M112,38 L130,58 L122,68"
                  stroke="white"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity="0.7"
                />
                <path
                  d="M88,142 L74,158 L82,164"
                  stroke="white"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity="0.65"
                />
                <path
                  d="M102,148 L116,162 L108,172"
                  stroke="white"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity="0.55"
                />
                {/* Impact point flash */}
                <circle cx="112" cy="96" r="6" fill="white" opacity="0.6" />
                <circle cx="112" cy="96" r="3" fill="white" opacity="0.9" />
              </>
            )}
          </svg>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export const CrackOverlay = memo(CrackOverlayComponent);
